import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { hasActiveSubscription } from "../features/payment/subscription/subscriptionStatus.js";

/**
 * Estado atual da família em relação ao fluxo de contratação:
 *
 *   'visitante'          → não autenticada
 *   'familia_cadastrada' → autenticada, família, sem assinatura ativa
 *   'familia_assinante'  → autenticada, família, com assinatura ativa
 *   'nao_familia'        → autenticada, mas é babá/admin (não usa o paywall de família)
 *
 * Usa o helper hasActiveSubscription já existente em subscriptionStatus.js.
 *
 * Retorna: { status, loading, familiaId, email, tipo }
 */
const FAMILIA_TIPOS = new Set(["cliente", "familia"]);

export function useFamiliaStatus() {
  const [state, setState] = useState({
    status: "visitante",
    loading: true,
    familiaId: null,
    email: null,
    tipo: null,
  });

  useEffect(() => {
    let cancelled = false;

    const resolve = (next) => {
      if (!cancelled) setState({ loading: false, ...next });
    };

    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        resolve({ status: "visitante", familiaId: null, email: null, tipo: null });
        return;
      }

      const userId = session.user.id;
      const email = session.user.email || null;

      const { data: usuario } = await supabase
        .from("usuarios")
        .select("id, tipo, is_admin, email")
        .eq("id", userId)
        .maybeSingle();

      const tipo = usuario?.tipo ?? null;
      const isFamilia = FAMILIA_TIPOS.has(tipo);

      if (!isFamilia) {
        resolve({ status: "nao_familia", familiaId: userId, email, tipo });
        return;
      }

      const ativo = await hasActiveSubscription({ userId, email: usuario?.email || email });

      resolve({
        status: ativo ? "familia_assinante" : "familia_cadastrada",
        familiaId: userId,
        email: usuario?.email || email,
        tipo,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export default useFamiliaStatus;
